import { FormEvent, ReactNode, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BellIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  CameraIcon,
  EnvelopeIcon,
  LinkIcon,
  MapPinIcon,
  PencilSquareIcon,
  PlusIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import {
  PortalAccountPermissionLevel,
  portalAccountMock,
} from './portalAccountMock';
import { usePortalState } from './PortalState';

type AccountTab = 'profile' | 'company' | 'users' | 'permissions' | 'notifications' | 'integrations';

type PortalAccountPageProps = {
  mode?: 'profile' | 'password';
};

const tabs: { id: AccountTab; label: string }[] = [
  { id: 'profile', label: 'Perfil' },
  { id: 'company', label: 'Empresa' },
  { id: 'users', label: 'Usuários' },
  { id: 'permissions', label: 'Permissões' },
  { id: 'notifications', label: 'Notificações' },
  { id: 'integrations', label: 'Integrações' },
];

const permissionLabel: Record<PortalAccountPermissionLevel, string> = {
  full: 'Acesso total',
  edit: 'Editar',
  view: 'Visualizar',
  none: 'Sem acesso',
};

const permissionClass: Record<PortalAccountPermissionLevel, string> = {
  full: 'bg-emerald-50 text-emerald-700',
  edit: 'bg-primary-50 text-primary-700',
  view: 'bg-secondary-100 text-secondary-700',
  none: 'bg-red-50 text-red-700',
};

function isAccountTab(value: string | null): value is AccountTab {
  return tabs.some((tab) => tab.id === value);
}

export function PortalAccountPage({ mode = 'profile' }: PortalAccountPageProps) {
  const { data, updateProfile } = usePortalState();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: AccountTab = isAccountTab(tabParam) ? tabParam : 'profile';

  const [profile, setProfile] = useState({
    ...portalAccountMock.profile,
    name: data.clientName || portalAccountMock.profile.name,
    email: data.clientEmail || portalAccountMock.profile.email,
  });
  const [company, setCompany] = useState(portalAccountMock.company);
  const [users] = useState(portalAccountMock.users);
  const [permissions, setPermissions] = useState(portalAccountMock.permissions);
  const [events, setEvents] = useState(portalAccountMock.notificationEvents);
  const [channels, setChannels] = useState(portalAccountMock.notificationChannels);
  const [integrations, setIntegrations] = useState(portalAccountMock.integrations);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const invitePendingCount = useMemo(
    () => users.filter((user) => user.statusLabel.toLowerCase().includes('pendente')).length,
    [users],
  );

  function setTab(tab: AccountTab) {
    setSearchParams(tab === 'profile' ? {} : { tab });
    setMessage(null);
    setError(null);
  }

  function saveProfile(event: FormEvent) {
    event.preventDefault();
    if (!profile.name.trim() || !profile.email.trim()) {
      setError('Informe nome e e-mail.');
      setMessage(null);
      return;
    }
    updateProfile(profile.name.trim(), profile.email.trim());
    setEditingProfile(false);
    setError(null);
    setMessage('Perfil atualizado nesta pré-visualização.');
  }

  function saveCompany(event: FormEvent) {
    event.preventDefault();
    setEditingCompany(false);
    setMessage('Dados da empresa atualizados nesta pré-visualização.');
  }

  function savePassword(event: FormEvent) {
    event.preventDefault();
    if (!currentPassword || !nextPassword) {
      setError('Informe a senha atual e a nova senha.');
      setMessage(null);
      return;
    }
    if (nextPassword !== confirmPassword) {
      setError('A confirmação não confere com a nova senha.');
      setMessage(null);
      return;
    }
    setCurrentPassword('');
    setNextPassword('');
    setConfirmPassword('');
    setError(null);
    setMessage('Senha atualizada nesta pré-visualização.');
  }

  if (mode === 'password') {
    return (
      <div className="mx-auto max-w-xl space-y-5">
        <div>
          <Link to="/portal/account" className="text-sm font-semibold text-primary-700 hover:underline">
            ← Voltar para Minha Conta
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-secondary-900">Alterar senha</h1>
          <p className="mt-1 text-sm text-secondary-600">Troca usada só nesta pré-visualização.</p>
        </div>
        <form onSubmit={savePassword} className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
          <div className="space-y-4">
            <Field label="Senha atual">
              <input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="erp-input"
                autoComplete="current-password"
              />
            </Field>
            <Field label="Nova senha">
              <input
                type="password"
                value={nextPassword}
                onChange={(event) => setNextPassword(event.target.value)}
                className="erp-input"
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirmar nova senha">
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="erp-input"
                autoComplete="new-password"
              />
            </Field>
            {error ? <p className="text-sm text-red-700">{error}</p> : null}
            {message ? <p className="text-sm text-secondary-700">{message}</p> : null}
            <button type="submit" className="erp-btn-primary">
              Salvar senha
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Minha Conta</h1>
        <p className="mt-1 max-w-3xl text-sm text-secondary-600">
          Gerencie suas informações, usuários, permissões e preferências de notificação.
        </p>
      </div>

      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex flex-wrap gap-1" aria-label="Minha Conta">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              className={`rounded-none border-b-2 px-4 py-2.5 text-sm font-semibold ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {message ? (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {message}
        </p>
      ) : null}
      {error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
      ) : null}

      {activeTab === 'profile' ? (
        <div className="grid gap-4 xl:grid-cols-2">
          <section className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-base font-semibold text-secondary-900">Meu perfil</h2>
              <button
                type="button"
                onClick={() => setEditingProfile((current) => !current)}
                className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1.5"
              >
                <PencilSquareIcon className="h-4 w-4" aria-hidden />
                {editingProfile ? 'Cancelar' : 'Editar perfil'}
              </button>
            </div>

            <div className="mt-5 flex items-center gap-4">
              <div className="relative">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-700">
                  {profile.avatarInitials}
                </span>
                <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border border-secondary-200 bg-white text-secondary-600">
                  <CameraIcon className="h-4 w-4" aria-hidden />
                </span>
              </div>
              <div>
                <p className="text-lg font-semibold text-secondary-900">{profile.name}</p>
                <p className="text-sm text-secondary-600">{profile.role}</p>
                <p className="text-sm text-secondary-500">{profile.email}</p>
              </div>
            </div>

            <form onSubmit={saveProfile} className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field label="Nome completo">
                <input
                  value={profile.name}
                  disabled={!editingProfile}
                  onChange={(event) => setProfile((current) => ({ ...current, name: event.target.value }))}
                  className="erp-input disabled:bg-secondary-50"
                />
              </Field>
              <Field label="Cargo">
                <input
                  value={profile.role}
                  disabled={!editingProfile}
                  onChange={(event) => setProfile((current) => ({ ...current, role: event.target.value }))}
                  className="erp-input disabled:bg-secondary-50"
                />
              </Field>
              <Field label="Telefone">
                <input
                  value={profile.phone}
                  disabled={!editingProfile}
                  onChange={(event) => setProfile((current) => ({ ...current, phone: event.target.value }))}
                  className="erp-input disabled:bg-secondary-50"
                />
              </Field>
              <Field label="E-mail">
                <input
                  type="email"
                  value={profile.email}
                  disabled={!editingProfile}
                  onChange={(event) => setProfile((current) => ({ ...current, email: event.target.value }))}
                  className="erp-input disabled:bg-secondary-50"
                />
              </Field>
              <Field label="Fuso horário">
                <select
                  value={profile.timezone}
                  disabled={!editingProfile}
                  onChange={(event) => setProfile((current) => ({ ...current, timezone: event.target.value }))}
                  className="erp-input disabled:bg-secondary-50"
                >
                  <option>(GMT-03:00) São Paulo</option>
                  <option>(GMT-04:00) Manaus</option>
                  <option>(GMT+00:00) Londres</option>
                </select>
              </Field>
              <Field label="Idioma">
                <select
                  value={profile.language}
                  disabled={!editingProfile}
                  onChange={(event) => setProfile((current) => ({ ...current, language: event.target.value }))}
                  className="erp-input disabled:bg-secondary-50"
                >
                  <option>Português (Brasil)</option>
                  <option>English (US)</option>
                  <option>Español</option>
                </select>
              </Field>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                {editingProfile ? (
                  <button type="submit" className="erp-btn-primary">
                    Salvar perfil
                  </button>
                ) : null}
                <Link to="/portal/account/password" className="text-sm font-semibold text-primary-700 hover:underline">
                  Alterar senha
                </Link>
              </div>
            </form>
          </section>

          <CompanyCard
            company={company}
            editing={editingCompany}
            onToggleEdit={() => setEditingCompany((current) => !current)}
            onChange={setCompany}
            onSubmit={saveCompany}
          />
        </div>
      ) : null}

      {activeTab === 'company' ? (
        <CompanyCard
          company={company}
          editing={editingCompany}
          onToggleEdit={() => setEditingCompany((current) => !current)}
          onChange={setCompany}
          onSubmit={saveCompany}
          wide
        />
      ) : null}

      {activeTab === 'users' ? (
        <section className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-100 px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-secondary-900">Usuários</h2>
              <p className="mt-1 text-sm text-secondary-500">
                {users.length} usuários · {invitePendingCount} convite(s) pendente(s)
              </p>
            </div>
            <button type="button" className="erp-btn-primary erp-btn-sm inline-flex items-center gap-1.5">
              <PlusIcon className="h-4 w-4" aria-hidden />
              Convidar usuário
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Usuário</th>
                  <th className="px-3 py-3 font-medium">Papel</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium">Último acesso</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-t border-secondary-100">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                          {user.name
                            .split(/\s+/)
                            .slice(0, 2)
                            .map((part) => part[0])
                            .join('')
                            .toUpperCase()}
                        </span>
                        <div>
                          <p className="font-semibold text-secondary-900">{user.name}</p>
                          <p className="text-xs text-secondary-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-secondary-700">{user.roleLabel}</td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          user.statusLabel.includes('pendente')
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {user.statusLabel}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-secondary-600">{user.lastAccessLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === 'permissions' ? (
        <section className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
          <div className="border-b border-secondary-100 px-5 py-4">
            <h2 className="text-base font-semibold text-secondary-900">Permissões</h2>
            <p className="mt-1 text-sm text-secondary-500">
              Matriz simplificada dos acessos da sua organização no portal.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Módulo</th>
                  <th className="px-3 py-3 font-medium">Nível</th>
                  <th className="px-3 py-3 font-medium">Alterar</th>
                </tr>
              </thead>
              <tbody>
                {permissions.map((row) => (
                  <tr key={row.id} className="border-t border-secondary-100">
                    <td className="px-5 py-3 font-medium text-secondary-900">{row.moduleLabel}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${permissionClass[row.level]}`}>
                        {permissionLabel[row.level]}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <select
                        value={row.level}
                        onChange={(event) =>
                          setPermissions((current) =>
                            current.map((item) =>
                              item.id === row.id
                                ? { ...item, level: event.target.value as PortalAccountPermissionLevel }
                                : item,
                            ),
                          )
                        }
                        className="rounded-lg border border-secondary-300 px-2.5 py-1.5 text-sm"
                      >
                        {(Object.keys(permissionLabel) as PortalAccountPermissionLevel[]).map((level) => (
                          <option key={level} value={level}>
                            {permissionLabel[level]}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === 'notifications' ? (
        <section className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
            <div>
              <h2 className="text-base font-semibold text-secondary-900">Preferências de notificação</h2>
              <p className="mt-1 text-sm text-secondary-500">
                Escolha como e quando deseja ser notificado sobre as atualizações dos seus projetos.
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {events.map((eventItem) => (
                  <label
                    key={eventItem.id}
                    className="flex items-start gap-3 rounded-xl border border-secondary-100 px-3 py-3 text-sm text-secondary-800"
                  >
                    <input
                      type="checkbox"
                      checked={eventItem.enabled}
                      onChange={() =>
                        setEvents((current) =>
                          current.map((item) =>
                            item.id === eventItem.id ? { ...item, enabled: !item.enabled } : item,
                          ),
                        )
                      }
                      className="mt-0.5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>{eventItem.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-secondary-200 bg-secondary-50/60 p-4">
              <h3 className="text-sm font-semibold text-secondary-900">Canais de notificação</h3>
              <ul className="mt-4 space-y-3">
                {channels.map((channel) => (
                  <li
                    key={channel.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-secondary-200 bg-white px-3 py-2.5"
                  >
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-secondary-800">
                      <ChannelIcon kind={channel.icon} />
                      {channel.label}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={channel.enabled}
                      onClick={() =>
                        setChannels((current) =>
                          current.map((item) =>
                            item.id === channel.id ? { ...item, enabled: !item.enabled } : item,
                          ),
                        )
                      }
                      className={`relative h-6 w-11 rounded-full transition-colors ${
                        channel.enabled ? 'bg-primary-500' : 'bg-secondary-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          channel.enabled ? 'left-5' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === 'integrations' ? (
        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-secondary-900">Integrações</h2>
            <p className="mt-1 text-sm text-secondary-500">
              Conexões opcionais disponíveis para a sua organização.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {integrations.map((item) => (
              <article key={item.id} className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-semibold text-secondary-900">{item.name}</h3>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      item.connected ? 'bg-emerald-50 text-emerald-700' : 'bg-secondary-100 text-secondary-600'
                    }`}
                  >
                    {item.statusLabel}
                  </span>
                </div>
                <p className="mt-2 text-sm text-secondary-600">{item.description}</p>
                <button
                  type="button"
                  disabled={item.statusLabel.toLowerCase().includes('indisponível')}
                  onClick={() =>
                    setIntegrations((current) =>
                      current.map((entry) =>
                        entry.id === item.id
                          ? {
                              ...entry,
                              connected: !entry.connected,
                              statusLabel: entry.connected ? 'Não conectado' : 'Conectado',
                            }
                          : entry,
                      ),
                    )
                  }
                  className="erp-btn-secondary erp-btn-sm mt-4 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {item.connected ? 'Desconectar' : 'Conectar'}
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function CompanyCard({
  company,
  editing,
  onToggleEdit,
  onChange,
  onSubmit,
  wide = false,
}: {
  company: typeof portalAccountMock.company;
  editing: boolean;
  onToggleEdit: () => void;
  onChange: (next: typeof portalAccountMock.company) => void;
  onSubmit: (event: FormEvent) => void;
  wide?: boolean;
}) {
  return (
    <section className={`rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm ${wide ? 'max-w-3xl' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-base font-semibold text-secondary-900">Empresa</h2>
        <button
          type="button"
          onClick={onToggleEdit}
          className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1.5"
        >
          <PencilSquareIcon className="h-4 w-4" aria-hidden />
          {editing ? 'Cancelar' : 'Editar dados'}
        </button>
      </div>

      <div className="mt-5 flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
          <BuildingOffice2Icon className="h-7 w-7" aria-hidden />
        </span>
        <div>
          <p className="text-lg font-semibold text-secondary-900">{company.name}</p>
          <p className="text-sm text-secondary-500">{company.document}</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        {editing ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Razão social">
              <input
                value={company.name}
                onChange={(event) => onChange({ ...company, name: event.target.value })}
                className="erp-input"
              />
            </Field>
            <Field label="CNPJ">
              <input
                value={company.document}
                onChange={(event) => onChange({ ...company, document: event.target.value })}
                className="erp-input"
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Endereço">
                <input
                  value={company.address}
                  onChange={(event) => onChange({ ...company, address: event.target.value })}
                  className="erp-input"
                />
              </Field>
            </div>
            <Field label="Setor">
              <input
                value={company.sector}
                onChange={(event) => onChange({ ...company, sector: event.target.value })}
                className="erp-input"
              />
            </Field>
            <Field label="Site">
              <input
                value={company.website}
                onChange={(event) => onChange({ ...company, website: event.target.value })}
                className="erp-input"
              />
            </Field>
            <div className="sm:col-span-2">
              <button type="submit" className="erp-btn-primary">
                Salvar empresa
              </button>
            </div>
          </div>
        ) : (
          <ul className="space-y-3 text-sm text-secondary-700">
            <InfoRow icon={<MapPinIcon className="h-4 w-4" aria-hidden />} label="Endereço" value={company.address} />
            <InfoRow
              icon={<BuildingLibraryIcon className="h-4 w-4" aria-hidden />}
              label="Setor"
              value={company.sector}
            />
            <InfoRow
              icon={<LinkIcon className="h-4 w-4" aria-hidden />}
              label="Site"
              value={
                <a
                  href={`https://${company.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-primary-700 hover:underline"
                >
                  {company.website}
                </a>
              }
            />
            <InfoRow
              icon={<UserIcon className="h-4 w-4" aria-hidden />}
              label="Contato principal"
              value={`${company.contactName} · ${company.contactEmail} · ${company.contactPhone}`}
            />
          </ul>
        )}
      </form>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="erp-label">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function InfoRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 text-secondary-400">{icon}</span>
      <div>
        <p className="text-xs font-medium text-secondary-500">{label}</p>
        <div className="mt-0.5 text-secondary-800">{value}</div>
      </div>
    </li>
  );
}

function ChannelIcon({ kind }: { kind: 'email' | 'whatsapp' | 'system' }) {
  if (kind === 'email') return <EnvelopeIcon className="h-4 w-4 text-secondary-600" aria-hidden />;
  if (kind === 'system') return <BellIcon className="h-4 w-4 text-secondary-600" aria-hidden />;
  return (
    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-bold text-white">
      W
    </span>
  );
}
